/**
 * `crypto/aes` — AES-GCM encryption / decryption via SubtleCrypto.
 *
 * Two key modes:
 *
 *   pbkdf2-256k  — derive a 256-bit AES key from a password using
 *                   PBKDF2-SHA-256 with 256 000 rounds. The salt is
 *                   16 random bytes (or whatever the caller supplies).
 *                   Round count is OWASP 2023's recommendation
 *                   (240 000+) rounded up to the nearest 1 000.
 *
 *   raw-key      — caller supplies the 32-byte AES key directly. No
 *                   derivation. Useful when integrating with a
 *                   server-side keystore.
 *
 * Output `combined` field packs the encrypted envelope as a single
 * base64 blob in this layout:
 *
 *   [ 1 byte  : version (0x01) ]
 *   [ 1 byte  : key mode (0x00 = pbkdf2-256k, 0x01 = raw-key)   ]
 *   [ 16 bytes: salt (zero-filled when key mode = raw-key)       ]
 *   [ 12 bytes: IV  (GCM standard nonce length)                  ]
 *   [ var    : ciphertext + GCM tag                              ]
 *
 * Versioned so we can change the layout without silently producing
 * undecodable output.
 *
 * GCM IV: 12 bytes, freshly random per encryption — NIST SP 800-38D.
 *
 * GCM tag: AES-GCM in WebCrypto already appends the auth tag to the
 * ciphertext output, so we don't need to handle it separately.
 */

const VERSION = 0x01;
const MODE_PBKDF2 = 0x00;
const MODE_RAW = 0x01;
const SALT_BYTES = 16;
const IV_BYTES = 12;
const PBKDF2_ROUNDS = 256_000;
const KEY_BITS = 256;

export type KeyDerivation = 'pbkdf2-256k' | 'raw-key';

export interface AesEncryptInput {
  plaintext: string | Uint8Array;
  /** Required when mode = pbkdf2-256k. */
  password?: string;
  /** Required when mode = raw-key. Must be exactly 32 bytes. */
  rawKey?: Uint8Array;
  /** Optional override for testing. Must be 16 bytes. */
  salt?: Uint8Array;
  /** Optional override for testing. Must be 12 bytes. */
  iv?: Uint8Array;
}

export interface AesEncryptOutput {
  ciphertext: Uint8Array;
  iv: Uint8Array;
  /** Present only when mode = pbkdf2-256k. */
  salt?: Uint8Array;
  /** Single-base64 envelope (header + salt + iv + ciphertext+tag). */
  combined: string;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const TEXT_ENC = new TextEncoder();
const TEXT_DEC = new TextDecoder('utf-8', { fatal: false });

function asUint8(input: string | Uint8Array): Uint8Array {
  return typeof input === 'string' ? TEXT_ENC.encode(input) : input;
}

function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let bin = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const cleaned = b64.replace(/\s+/g, '');
  if (!cleaned) return new Uint8Array(0);
  const bin = atob(cleaned);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.byteLength;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.byteLength;
  }
  return out;
}

async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    TEXT_ENC.encode(password) as BufferSource,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: salt as BufferSource,
      iterations: PBKDF2_ROUNDS,
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function importRawAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  if (rawKey.byteLength !== KEY_BITS / 8) {
    throw new Error(`raw key must be ${KEY_BITS / 8} bytes (got ${rawKey.byteLength})`);
  }
  return crypto.subtle.importKey(
    'raw',
    rawKey as BufferSource,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export async function aesEncrypt(input: AesEncryptInput): Promise<AesEncryptOutput> {
  const usingPassword = typeof input.password === 'string' && input.password.length > 0;
  const usingRawKey = !!input.rawKey;
  if (usingPassword === usingRawKey) {
    throw new Error('aesEncrypt: must supply exactly one of `password` or `rawKey`');
  }

  const salt =
    input.salt ??
    (usingPassword
      ? crypto.getRandomValues(new Uint8Array(SALT_BYTES))
      : new Uint8Array(SALT_BYTES) /* zero-filled placeholder for raw mode */);
  if (salt.byteLength !== SALT_BYTES) {
    throw new Error(`salt must be ${SALT_BYTES} bytes (got ${salt.byteLength})`);
  }

  const iv = input.iv ?? crypto.getRandomValues(new Uint8Array(IV_BYTES));
  if (iv.byteLength !== IV_BYTES) {
    throw new Error(`iv must be ${IV_BYTES} bytes (got ${iv.byteLength})`);
  }

  const key = usingPassword
    ? await deriveKeyFromPassword(input.password!, salt)
    : await importRawAesKey(input.rawKey!);

  const plaintextBytes = asUint8(input.plaintext);
  const ctBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    plaintextBytes as BufferSource,
  );
  const ciphertext = new Uint8Array(ctBuffer);

  const header = new Uint8Array([VERSION, usingPassword ? MODE_PBKDF2 : MODE_RAW]);
  const combined = bytesToBase64(concat([header, salt, iv, ciphertext]));

  return {
    ciphertext,
    iv,
    salt: usingPassword ? salt : undefined,
    combined,
  };
}

export interface AesDecryptOutput {
  plaintext: string;
  bytes: Uint8Array;
}

export async function aesDecrypt(
  combinedB64: string,
  password?: string,
  rawKey?: Uint8Array,
): Promise<AesDecryptOutput> {
  const blob = base64ToBytes(combinedB64);
  if (blob.byteLength < 1 + 1 + SALT_BYTES + IV_BYTES + 16) {
    throw new Error('密文长度过短，不是合法的 AES-GCM 信封');
  }
  if (blob[0] !== VERSION) {
    throw new Error(`未知信封版本 0x${blob[0].toString(16)}`);
  }
  const mode = blob[1];
  if (mode !== MODE_PBKDF2 && mode !== MODE_RAW) {
    throw new Error(`未知密钥模式 0x${mode.toString(16)}`);
  }

  const salt = blob.subarray(2, 2 + SALT_BYTES);
  const iv = blob.subarray(2 + SALT_BYTES, 2 + SALT_BYTES + IV_BYTES);
  const ciphertext = blob.subarray(2 + SALT_BYTES + IV_BYTES);

  let key: CryptoKey;
  if (mode === MODE_PBKDF2) {
    if (!password) throw new Error('密文是密码模式，但未提供 password');
    key = await deriveKeyFromPassword(password, salt);
  } else {
    if (!rawKey) throw new Error('密文是 raw-key 模式，但未提供 rawKey');
    key = await importRawAesKey(rawKey);
  }

  let pt: ArrayBuffer;
  try {
    pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      ciphertext as BufferSource,
    );
  } catch {
    throw new Error('解密失败：密码 / 密钥不正确，或密文已损坏');
  }
  const bytes = new Uint8Array(pt);
  return { plaintext: TEXT_DEC.decode(bytes), bytes };
}
